import React, {useContext, useState, useMemo} from "react";
import "./ContentBusinessDeal.scss";
import {EventUpdatedContext} from "../../../store/EventUpdatedContext";
import {getIdWithToken} from "../../../utils/storage/Token";
import {DeleteBusiness, useFetchBusinessDealContent} from "../../../service/Business/BusinessService";
import FCSpinner from "../../FCObjets/FCSpinner";
import {TypeForm} from "../../../types/EnumTypeForm";
import {BusinessDealInterface} from "../../../service/Business/Interfaces/BusinessDealInterface";
import StatusBusinessDeal from "../../../utils/Business/StatusBusinessDeal";
import {TypeStatusBusinessDeal} from "../../../utils/Business/StatusBusinessDeal";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import {EnumTypeMail} from "../../../types/Business/EnumTypeMail";
import FCContentModalFL, {DataForm} from "../../FCObjets/Form/FCContentModalFL";
import FCTag from "../../FCObjets/FCTag";
import FCToggleSwitch from "../../FCObjets/FCToggleSwitch";
import FCSortableHeader from "../../FCObjets/FCSortableHeader";
import {useTableSort} from "../../../hook/useTableSort";
import formatEuro from "../../../utils/formatEuro";
import {useToast} from "../../FCObjets/FCToast/FCToast";

type SortKey = "client" | "name" | "number" | "state" | "begin" | "amount";

interface BusinessDealContentItem {
    typeMail?: EnumTypeMail;
    id?: number;
    business: BusinessDealInterface;
    totalToInvoiced: number;
    totalInvoiced: number;
    remainToInvoice: number;
    totalInvoiceOpen: number;
}

function ContentBusinessDeal() {
    const toast = useToast();

    let {cptUpdated, setUpdated} = useContext(EventUpdatedContext);
    const {data: business, loading, error} = useFetchBusinessDealContent(getIdWithToken(), cptUpdated);
    const [searchQuery, setSearchQuery] = useState("");
    const [showEnded, setShowEnded] = useState(false);
    const [data, setData] = useState<DataForm>({
        type: TypeForm.EMPTY,
        data: undefined,
    });

    /**
     * Gestion de l'ouverture du modal
     */
    const gestionModal = (open: boolean, e: BusinessDealInterface | any | undefined, type: TypeForm | undefined) => {
        setData({type: ((open && type) ? type : TypeForm.EMPTY), data: e});
    }


    /**
     * Formate une date
     */
    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {day: '2-digit', month: 'short'});
    };

    /**
     * Formate une période
     */
    const formatPeriod = (begin: string, end: string): string => {
        return `${formatDate(begin)} - ${formatDate(end)}`;
    };

    /**
     * Calcule le pourcentage de facturation
     */
    const getInvoiceProgress = (item: BusinessDealContentItem): number => {
        if (item.totalToInvoiced <= 0) return 0;
        return Math.min(100, Math.round((item.totalInvoiced / item.totalToInvoiced) * 100));
    };

    /**
     * Contrats filtrés et triés
     */
    const filteredBusiness = useMemo(() => {
        if (!business) return [];
        return business.filter((item: BusinessDealContentItem) => {
            // Filtre terminés/annulés
            if (!showEnded && (item.business.state === TypeStatusBusinessDeal.ENDED || item.business.state === TypeStatusBusinessDeal.CANCELED)) {
                return false;
            }
            // Filtre recherche
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                return item.business.customer.companyName.toLowerCase().includes(q) ||
                    item.business.name.toLowerCase().includes(q) ||
                    item.business.details.number.toLowerCase().includes(q) ||
                    item.business.description.toLowerCase().includes(q);
            }
            return true;
        });
    }, [business, showEnded, searchQuery]);

    /**
     * Tri des contrats — accesseurs par colonne
     */
    const {sortedData: sortedBusiness, sortState, handleSort} = useTableSort<BusinessDealContentItem, SortKey>(
        filteredBusiness,
        {
            client: (item) => item.business.customer.companyName,
            name: (item) => item.business.name,
            number: (item) => item.business.details.number,
            state: (item) => item.business.state,
            begin: (item) => new Date(item.business.details.begin),
            amount: (item) => item.totalToInvoiced,
        },
    );

    /**
     * Statistiques KPI
     */
    const stats = useMemo(() => {
        if (!business) return {volume: 0, evolution: null, actifs: 0, termines: 0, total: 0};
        const all = business as BusinessDealContentItem[];
        const volume = all.reduce((sum: number, item: BusinessDealContentItem) => sum + item.totalInvoiced, 0);
        const actifs = all.filter((item: BusinessDealContentItem) =>
            item.business.state === TypeStatusBusinessDeal.IN_PROGRESS ||
            item.business.state === TypeStatusBusinessDeal.INITIALIZED
        ).length;
        const termines = all.filter((item: BusinessDealContentItem) =>
            item.business.state === TypeStatusBusinessDeal.ENDED
        ).length;
        return {volume, evolution: null, actifs, termines, total: all.length};
    }, [business]);

    return (
        <FCContentModalFL form={data} onModalFormFinished={() => gestionModal(false, undefined, undefined)}>
            <div className="contractPage">
                {/* Header */}
                <div className="contractHeader">
                    <div className="contractHeaderLeft">
                        <h1 className="contractTitle">Gestion des Contrats</h1>
                        <p className="contractSubtitle">Gérez vos engagements professionnels et suivez vos collaborations.</p>
                    </div>
                    <div className="contractHeaderRight">
                        <div className="contractSearchBar">
                            <span className="material-symbols-outlined contractSearchIcon">search</span>
                            <input
                                type="text"
                                className="contractSearchInput"
                                placeholder="Rechercher un contrat..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button className="contractBtnAdd" onClick={() => gestionModal(true, undefined, TypeForm.BUSINESS)}>
                            <span className="material-symbols-outlined">add</span>
                            Ajouter un contrat
                        </button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="contractKpiGrid">
                    <div className="contractKpiCard contractKpiVolume">
                        <div className="contractKpiLabel">Volume d'affaires total</div>
                        <div className="contractKpiValue">{formatEuro(stats.volume, 0)}</div>
                        {stats.evolution !== null && (
                            <div className="contractKpiEvolution positive">
                                +{stats.evolution}% par rapport au mois dernier
                            </div>
                        )}
                    </div>
                    <div className="contractKpiCard">
                        <div className="contractKpiBadge badgeActive">{stats.actifs} Actifs</div>
                        <div className="contractKpiBadge badgeEnded">{stats.termines} Terminés</div>
                        <div className="contractKpiLabel">Nombre de contrat total</div>
                        <div className="contractKpiValueLg">{stats.total}</div>
                    </div>
                </div>

                {/* Toggle contrats terminés + titre section */}
                <div className="contractSectionHeader">
                    <h2 className="contractSectionTitle">Répertoire des Contrats</h2>
                    <div className="contractSectionActions">
                        <FCToggleSwitch
                            label="Afficher terminés"
                            defaultChecked={showEnded}
                            onToggle={(checked) => setShowEnded(checked)}
                        />
                    </div>
                </div>

                {/* Loading */}
                {loading && (
                    <div className="contractLoading">
                        <FCSpinner size="large"/>
                    </div>
                )}

                {/* Error */}
                {error && <FCAlertMsgErrorAPI error={error}/>}

                {/* Desktop table */}
                {!loading && !error && (
                    <div className="contractTableWrapper">
                        <table className="contractTable">
                            <thead>
                            <tr>
                                <th><FCSortableHeader label="Client" sortKey="client" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Contrat" sortKey="name" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Identifiant" sortKey="number" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Statut" sortKey="state" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Période" sortKey="begin" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Montant" sortKey="amount" sortState={sortState} onSort={handleSort}/></th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedBusiness.length > 0 ? (
                                sortedBusiness.map((item: BusinessDealContentItem, index: React.Key) => {
                                    const progress = getInvoiceProgress(item);
                                    return (
                                        <tr key={index}>
                                            <td>
                                                <div className="contractCellClient">
                                                    <div className="contractClientAvatar">
                                                        <span className="material-symbols-outlined">apartment</span>
                                                    </div>
                                                    <div>
                                                        <div className="contractClientName">{item.business.customer.companyName}</div>
                                                        <div className="contractClientDesc">{item.business.description}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="contractCellName">{item.business.name}</div>
                                            </td>
                                            <td>
                                                <span className="contractCellId">{item.business.details.number}</span>
                                            </td>
                                            <td>
                                                <FCTag value={StatusBusinessDeal.toString(item.business.state)}
                                                       color={StatusBusinessDeal.toColor(item.business.state)}
                                                       style={{fontWeight: "bold", fontSize: "11px"}}/>
                                            </td>
                                            <td>
                                                <div className="contractCellPeriod">
                                                    {formatPeriod(item.business.details.begin, item.business.details.end)}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="contractCellAmount">
                                                    <div className="contractAmountValue">{formatEuro(item.totalToInvoiced)}</div>
                                                    <div className="contractProgressMini">
                                                        <div className="contractProgressMiniBar">
                                                            <div className="contractProgressMiniFill" style={{width: `${progress}%`}}></div>
                                                        </div>
                                                        <span className="contractProgressMiniText">{progress}%</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="contractCellActions">
                                                    <button className="contractActionBtn contractActionEdit"
                                                            onClick={() => gestionModal(true, item.business, TypeForm.BUSINESS)}
                                                            title="Modifier">
                                                        <span className="material-symbols-outlined">edit</span>
                                                    </button>
                                                    <button className="contractActionBtn contractActionPdf"
                                                            onClick={() => window.open(`/pdf/business/${item.business.id}`, '_blank')}
                                                            title="Proposition commerciale">
                                                        <span className="material-symbols-outlined">picture_as_pdf</span>
                                                    </button>
                                                    <button className="contractActionBtn contractActionMail"
                                                            onClick={() => {
                                                                item.typeMail = EnumTypeMail.BUSINESS;
                                                                item.id = item.business.id;
                                                                gestionModal(true, item, TypeForm.SEND_MAIL);
                                                            }}
                                                            title="Envoyer contrat">
                                                        <span className="material-symbols-outlined">mail</span>
                                                    </button>
                                                    <button className="contractActionBtn contractActionArchive"
                                                            onClick={() => gestionModal(true, item.business, TypeForm.UPLOAD_BUSINESS)}
                                                            title="Archiver">
                                                        <span className="material-symbols-outlined">inventory_2</span>
                                                    </button>
                                                    <button className="contractActionBtn contractActionDelete"
                                                            onClick={() => {
                                                                const onConfirm = () => {
                                                                    DeleteBusiness(item.business).then(() => {
                                                                        toast.success("Contrat supprimé.", "Contrat");
                                                                        setUpdated();
                                                                    });
                                                                };
                                                                const payload = {
                                                                    title: `Suppression du contrat ${item.business.name}`,
                                                                    msg: "Êtes-vous sûr de vouloir supprimer ce contrat ?",
                                                                    onConfirm: onConfirm
                                                                };
                                                                gestionModal(true, payload, TypeForm.CONFIRM);
                                                            }}
                                                            title="Supprimer">
                                                        <span className="material-symbols-outlined">delete</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="contractEmptyState">Aucun contrat trouvé</div>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                        {sortedBusiness.length > 0 && (
                            <div className="contractTableFooter">
                                <span className="contractTableCount">
                                    Affichage de {sortedBusiness.length} sur {business ? business.length : 0} contrats
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Mobile cards */}
                {!loading && !error && (
                    <div className="contractMobileList">
                        {sortedBusiness.length > 0 ? (
                            sortedBusiness.map((item: BusinessDealContentItem, index: React.Key) => {
                                const progress = getInvoiceProgress(item);
                                return (
                                    <div className="contractMobileCard" key={index}>
                                        <div className="contractMobileCardHeader">
                                            <div className="contractMobileCardTags">
                                                <FCTag value={StatusBusinessDeal.toString(item.business.state)}
                                                       color={StatusBusinessDeal.toColor(item.business.state)}
                                                       style={{fontWeight: "bold", fontSize: "10px"}}/>
                                            </div>
                                        </div>
                                        <h3 className="contractMobileCardName">{item.business.name}</h3>
                                        <div className="contractMobileCardClient">{item.business.customer.companyName}</div>
                                        <div className="contractMobileCardMeta">
                                            <span className="material-symbols-outlined">calendar_today</span>
                                            {formatPeriod(item.business.details.begin, item.business.details.end)}
                                            <span className="contractMobileCardMetaSep">|</span>
                                            <span className="material-symbols-outlined">location_on</span>
                                            {item.business.customer.address || "—"}
                                        </div>
                                        <div className="contractMobileCardAmountRow">
                                            <span className="contractMobileCardAmount">{formatEuro(item.totalToInvoiced)}</span>
                                        </div>
                                        <div className="contractMobileCardProgress">
                                            <div className="contractMobileProgressBar">
                                                <div className="contractMobileProgressFill" style={{width: `${progress}%`}}></div>
                                            </div>
                                            <div className="contractMobileProgressInfo">
                                                <span>Facturé : {formatEuro(item.totalInvoiced)}</span>
                                                <span>{progress}%</span>
                                            </div>
                                        </div>
                                        <div className="contractMobileCardActions">
                                            <button className="contractMobileBtn"
                                                    onClick={() => gestionModal(true, item.business, TypeForm.BUSINESS)}>
                                                <span className="material-symbols-outlined">edit</span>
                                            </button>
                                            <button className="contractMobileBtn"
                                                    onClick={() => window.open(`/pdf/business/${item.business.id}`, '_blank')}>
                                                <span className="material-symbols-outlined">picture_as_pdf</span>
                                            </button>
                                            <button className="contractMobileBtn"
                                                    onClick={() => {
                                                        item.typeMail = EnumTypeMail.BUSINESS;
                                                        item.id = item.business.id;
                                                        gestionModal(true, item, TypeForm.SEND_MAIL);
                                                    }}>
                                                <span className="material-symbols-outlined">mail</span>
                                            </button>
                                            <button className="contractMobileBtn contractMobileBtnDelete"
                                                    onClick={() => {
                                                        const onConfirm = () => {
                                                            DeleteBusiness(item.business).then(() => {
                                                                toast.success("Contrat supprimé.", "Contrat");
                                                                setUpdated();
                                                            });
                                                        };
                                                        const payload = {
                                                            title: `Suppression du contrat ${item.business.name}`,
                                                            msg: "Êtes-vous sûr de vouloir supprimer ce contrat ?",
                                                            onConfirm: onConfirm
                                                        };
                                                        gestionModal(true, payload, TypeForm.CONFIRM);
                                                    }}>
                                                <span className="material-symbols-outlined">delete</span>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="contractEmptyState">Aucun contrat trouvé</div>
                        )}
                    </div>
                )}

            </div>
        </FCContentModalFL>
    );
}

export default ContentBusinessDeal;
